import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DollarSign, ChevronDown, ChevronRight, Search, Loader2, RefreshCw, Download, Calendar, X, Building, Building2, MapPin, Eye, Check, XCircle, AlertTriangle, Wallet, WalletCards, Receipt, ReceiptText, Settings2, Plus, FileText, Clock, Users, Filter, SlidersHorizontal } from 'lucide-react';
import { exportReportToCSV, getDateFilename } from '../../../utils/exportUtils';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useUserPreferences } from '../../../contexts/UserPreferencesContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import { reportsEarningsService } from '../../../services/reportsEarningsService';
import { overtimeService } from '../../../services/overtimeService';
import { settingsService } from '../../../services/settingsService';
import { paymentBatchService } from '../../../services/paymentBatchService';
import { useCorporateDashboardRealtime } from '../../../hooks/useRealtimeSubscription';
import ReportDetailModal from '../../../components/corporate/reports/ReportDetailModal';
import RejectModal from '../../../components/corporate/reports/RejectModal';
import DisputeModal from '../../../components/corporate/reports/DisputeModal';
import OvertimeApprovalModal from '../../../components/corporate/reports/OvertimeApprovalModal';
import ChargeAdjustmentModal from '../../../components/corporate/reports/ChargeAdjustmentModal';
import AdditionalChargesModal from '../../../components/corporate/reports/AdditionalChargesModal';
import Invoice from '../../../components/corporate/earnings/Invoice';
import FinancialSummaryCard from '../../../components/corporate/earnings/FinancialSummaryCard';
import RecordPaymentModal from '../../../components/corporate/earnings/RecordPaymentModal';
import PaymentDetailsModal from '../../../components/corporate/earnings/PaymentDetailsModal';
import AccessDenied from '../../../components/ui/AccessDenied';
import { cn } from '../../../utils/cn';
import { calculateInstanceFinancials, aggregateFinancials, formatCurrency, formatOvertime } from '../../../utils/earningsCalculations';

// Helper to transform snake_case job data to camelCase for Invoice component
const transformJobForInvoice = (job, instances = []) => {
  if (!job) return null;
  return {
    id: job.id,
    jobDisplayId: job.job_display_id || `JOB-${String(job.id).slice(-6).toUpperCase()}`,
    jobName: job.job_name,
    jobType: job.job_type,
    description: job.description,
    priority: job.priority,
    status: job.status,
    locationAddress: job.location_address,
    // Contractor payment fields
    paymentType: job.payment_type,
    paymentAmount: job.payment_amount,
    contractorIncludeGst: job.contractor_include_gst,
    contractorGstType: job.contractor_gst_type,
    // Client charge fields
    clientChargeType: job.client_charge_type,
    clientChargeAmount: job.client_charge_amount,
    clientIncludeGst: job.client_include_gst,
    clientGstType: job.client_gst_type,
    // Job configuration fields - used to determine if features were required
    photosRequiredBefore: job.photos_required_before,
    photosRequiredAfter: job.photos_required_after,
    checklistTemplate: job.checklist_template,
    questionnaireTemplate: job.questionnaire_template,
    clockInRadius: job.clock_in_radius,
    // Safety, Equipment, Other Details
    safetyRequirements: job.safety_requirements,
    equipmentList: job.equipment_list,
    otherDetails: job.other_details,
    // Other fields
    estimatedHours: job.estimated_hours,
    scheduledDate: job.scheduled_date,
    scheduledTime: job.scheduled_time,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    jobSource: job.job_source,
    serviceReceiver: job.service_receiver ? {
      id: job.service_receiver.id,
      receiver_name: job.service_receiver.receiver_name,
      receiver_type: job.service_receiver.receiver_type,
      contact_person: job.service_receiver.contact_person,
      contact_email: job.service_receiver.contact_email,
      contact_phone: job.service_receiver.contact_phone,
      address: job.service_receiver.address
    } : null,
    partnerAgency: job.partner_agency,
    recurringSchedule: job.recurring_schedule,
    job_instances: instances.map(inst => ({
      id: inst.id,
      instance_number: inst.instance_number,
      scheduled_date: inst.scheduled_date,
      scheduled_time: inst.scheduled_time,
      status: inst.status,
      contractor: inst.contractor
    }))
  };
};

const transformInstanceForInvoice = (instance) => {
  if (!instance) return null;
  return {
    id: instance.id,
    instance_number: instance.instance_number,
    scheduled_date: instance.scheduled_date,
    scheduled_time: instance.scheduled_time,
    status: instance.status,
    contractor: instance.contractor,
    // Financial fields
    charge_adjustment: instance.charge_adjustment,
    charge_adjustment_reason: instance.charge_adjustment_reason,
    client_payment_received: instance.client_payment_received,
    client_payment_received_at: instance.client_payment_received_at,
    additional_charges: instance.additional_charges,
    overtime_request: instance.overtime_request,
    clock_in_distance: instance.clock_in_distance
  };
};

// Helper to format decimal hours as "Xhr Ymn"
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0hr 0mn';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}hr ${minutes}mn`;
};

// Period filter options
const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom Range' }
];

// Tab options (kept for status filtering logic, UI removed)
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'submitted', label: 'Pending Review' },
  { key: 'overtime', label: 'Overtime Requests' },
  { key: 'approved', label: 'Approved' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'paid', label: 'Paid' }
];

// Helper to calculate date range based on period selection
const getDateRangeForPeriod = (period) => {
  const now = new Date();
  let startDate = null;
  let endDate = null;

  switch (period) {
    case 'current_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'last_6_months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      endDate = now;
      break;
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }

  return {
    startDate: startDate?.toISOString()?.split('T')[0],
    endDate: endDate?.toISOString()?.split('T')[0]
  };
};

// Color constants for alternating job site groups (matching Job Management)
const JOBSITE_COLORS = {
  odd: { jobSite: 'bg-background', job: 'bg-surface', instance: 'bg-surface-light' },
  even: { jobSite: 'bg-background-alt', job: 'bg-surface-alt', instance: 'bg-surface-alt-light' }
};

// Get timesheet status color
const getTimesheetStatusColor = (status) => {
  const colors = {
    submitted: 'bg-orange-500/20 text-orange-300',
    approved: 'bg-green-500/20 text-green-300',
    paid: 'bg-purple-500/20 text-purple-300',
    disputed: 'bg-red-500/20 text-red-300',
    rejected: 'bg-red-500/20 text-red-300'
  };
  return colors[status] || 'bg-gray-500/20 text-text-secondary';
};

// Format status text for display
const formatStatusText = (status) => {
  if (!status) return 'Submitted';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Format date for display
const formatDate = (dateString, dateFormat = 'DD/MM/YYYY') => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const day = d.getDate(); // No padding - "7" not "07"
    const monthShort = d.toLocaleDateString('en-AU', { month: 'short' });
    const yearShort = `'${String(d.getFullYear()).slice(-2)}`; // '26 not 2026

    switch (dateFormat) {
      case 'MM/DD/YYYY':
        return `${monthShort} ${day}, ${yearShort}`;
      case 'YYYY-MM-DD':
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      case 'DD/MM/YYYY':
      default:
        return `${day} ${monthShort} ${yearShort}`;
    }
  } catch {
    return dateString;
  }
};

// Group instances by Client → Job Site → Job hierarchy
const groupInstancesByHierarchy = (instances) => {
  const clientGroups = new Map();

  instances?.forEach(instance => {
    const job = instance?.job;
    if (!job) return;

    // Determine client
    const clientId = job?.client_id || 'unassigned';
    const clientName = job?.client?.client_name || 'Unassigned Client';
    const clientTag = job?.client?.tag || null;
    const clientContactPerson = job?.client?.contact_person || null;

    if (!clientGroups.has(clientId)) {
      clientGroups.set(clientId, {
        client: { id: clientId, name: clientName, tag: clientTag, contactPerson: clientContactPerson, isUnassigned: clientId === 'unassigned' },
        jobSites: new Map()
      });
    }

    const clientGroup = clientGroups.get(clientId);

    // Determine job site
    const jobSiteId = job?.job_site_id || 'unassigned';
    const jobSiteName = job?.job_site?.site_name || 'No Site';
    const jobSiteAddress = job?.job_site?.address || job?.location_address || null;

    if (!clientGroup.jobSites.has(jobSiteId)) {
      clientGroup.jobSites.set(jobSiteId, {
        jobSite: { id: jobSiteId, name: jobSiteName, address: jobSiteAddress, isUnassigned: jobSiteId === 'unassigned' },
        jobs: new Map()
      });
    }

    const jobSiteGroup = clientGroup.jobSites.get(jobSiteId);

    // Group by job
    const jobId = job?.id;
    if (!jobSiteGroup.jobs.has(jobId)) {
      jobSiteGroup.jobs.set(jobId, {
        job: job,
        instances: []
      });
    }

    jobSiteGroup.jobs.get(jobId).instances.push(instance);
  });

  // Sort client groups: Unassigned last, then alphabetically
  const sortedClientGroups = Array.from(clientGroups.entries()).sort((a, b) => {
    if (a[0] === 'unassigned') return 1;
    if (b[0] === 'unassigned') return -1;
    return a[1].client.name.localeCompare(b[1].client.name);
  });

  // Sort job sites within each client
  sortedClientGroups.forEach(([, clientData]) => {
    const sortedJobSites = Array.from(clientData.jobSites.entries()).sort((a, b) => {
      if (a[0] === 'unassigned') return 1;
      if (b[0] === 'unassigned') return -1;
      return a[1].jobSite.name.localeCompare(b[1].jobSite.name);
    });
    clientData.jobSites = new Map(sortedJobSites);
  });

  return new Map(sortedClientGroups);
};

export default function Earnings() {
  useAuth();
  const { currentCompany } = useCompany();
  const { preferences } = useUserPreferences();
  const { hasPermission } = usePermissions();

  // Permission check
  const canViewEarnings = hasPermission('view_earnings');

  // Data state
  const [jobs, setJobs] = useState([]);
  const [companyGstRate, setCompanyGstRate] = useState(10);
  const [showProfitColumn, setShowProfitColumn] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI state
  const [activeTab] = useState('all'); // Default to 'all' - tabs UI removed but state kept for filtering logic
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractor, setSelectedContractor] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Hierarchy expansion state
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [expandedJobSites, setExpandedJobSites] = useState(new Set());
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  // New filter state
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedJobSite, setSelectedJobSite] = useState('');

  // Modal state
  const [viewingReport, setViewingReport] = useState(null);
  const [rejectingInstance, setRejectingInstance] = useState(null);
  const [disputingInstance, setDisputingInstance] = useState(null);

  // Invoice modal state
  const [viewingJobInvoice, setViewingJobInvoice] = useState(null); // { job, instances }
  const [viewingInstanceInvoice, setViewingInstanceInvoice] = useState(null); // { job, instance }

  // Overtime state
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [overtimeCount, setOvertimeCount] = useState(0);
  const [reviewingOvertime, setReviewingOvertime] = useState(null);
  const [overtimeProcessing, setOvertimeProcessing] = useState(false);

  // Charge adjustment modals
  const [adjustingChargeInstance, setAdjustingChargeInstance] = useState(null);
  const [addingChargesInstance, setAddingChargesInstance] = useState(null);
  const [chargeSubmitting, setChargeSubmitting] = useState(false);

  // Selection state for bulk actions
  const [selectedInstances, setSelectedInstances] = useState(new Set());

  // Bulk payment modal state
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);

  // Single instance payment state (when clicking individual "Mark as Paid" button)
  const [singlePaymentInstance, setSinglePaymentInstance] = useState(null);

  // Payment details modal state (for viewing proof of payment)
  const [viewingPaymentDetails, setViewingPaymentDetails] = useState(null);

  // Transaction search results (set of instance IDs matching transaction ref search)
  const [transactionSearchResults, setTransactionSearchResults] = useState(new Set());

  // Skip realtime refresh until timestamp (to prevent reload after optimistic updates)
  const skipRealtimeUntil = React.useRef(0);

  // Load data
  const loadData = useCallback(async () => {
    if (!currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Always load overtime count for badge
      const pendingOvertimeCount = await overtimeService.getPendingOvertimeCount(currentCompany.id);
      setOvertimeCount(pendingOvertimeCount);

      // If on overtime tab, load overtime requests
      if (activeTab === 'overtime') {
        const overtimeData = await overtimeService.getOvertimeRequests(currentCompany.id, { status: 'pending' });
        setOvertimeRequests(overtimeData);
        setLoading(false);
        return;
      }

      // Calculate date range based on period selection
      let dateFilters = {};
      if (selectedPeriod === 'custom') {
        dateFilters = {
          startDate: customDateFrom || null,
          endDate: customDateTo || null
        };
      } else {
        dateFilters = getDateRangeForPeriod(selectedPeriod);
      }

      const filters = {
        status: activeTab === 'all' ? null : activeTab,
        contractorId: selectedContractor || null,
        clientId: selectedClient || null,
        jobSiteId: selectedJobSite || null,
        ...dateFilters
      };

      const [jobsData, counts, contractorsList] = await Promise.all([
        reportsEarningsService.getSubmittedJobs(currentCompany.id, filters),
        reportsEarningsService.getStatusCounts(currentCompany.id),
        reportsEarningsService.getContractorsWithSubmissions(currentCompany.id)
      ]);

      setJobs(jobsData);
      setStatusCounts(counts);
      setContractors(contractorsList);
    } catch (err) {
      console.error('Error loading earnings:', err);
      setError(err?.message || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, activeTab, selectedContractor, selectedPeriod, customDateFrom, customDateTo, selectedClient, selectedJobSite]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load company settings (GST rate, profit column visibility)
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentCompany?.id) return;
      try {
        const settings = await settingsService.getCompanySettings(currentCompany.id);
        if (settings?.gst_rate !== undefined) {
          setCompanyGstRate(settings.gst_rate);
        }
        if (settings?.hide_financials !== undefined) {
          setShowProfitColumn(!settings.hide_financials);
        }
      } catch (err) {
        console.error('Error loading company settings:', err);
      }
    };
    loadSettings();
  }, [currentCompany?.id]);

  // Real-time updates - refresh when timesheets or overtime requests change
  // Skip refresh if we just did an optimistic update (to prevent flicker)
  useCorporateDashboardRealtime(currentCompany?.id, () => {
    if (Date.now() < skipRealtimeUntil.current) {
      return;
    }
    loadData();
  });

  // Extract unique clients from loaded jobs
  const clients = useMemo(() => {
    const clientMap = new Map();
    jobs?.forEach(instance => {
      const client = instance?.job?.client;
      if (client && !clientMap.has(client.id)) {
        clientMap.set(client.id, client);
      }
    });
    return Array.from(clientMap.values());
  }, [jobs]);

  // Extract unique job sites from loaded jobs
  const jobSites = useMemo(() => {
    const siteMap = new Map();
    jobs?.forEach(instance => {
      const site = instance?.job?.job_site;
      if (site && !siteMap.has(site.id)) {
        siteMap.set(site.id, site);
      }
    });
    return Array.from(siteMap.values());
  }, [jobs]);

  // Search by transaction ID when search term looks like a transaction ref
  useEffect(() => {
    const searchByTransaction = async () => {
      if (!currentCompany?.id || !searchTerm || searchTerm.length < 3) {
        setTransactionSearchResults(new Set());
        return;
      }

      try {
        const instanceIds = await paymentBatchService.searchByTransaction(currentCompany.id, searchTerm);
        setTransactionSearchResults(new Set(instanceIds));
      } catch (err) {
        console.error('Error searching by transaction:', err);
        setTransactionSearchResults(new Set());
      }
    };

    // Debounce the transaction search
    const timeoutId = setTimeout(searchByTransaction, 300);
    return () => clearTimeout(timeoutId);
  }, [currentCompany?.id, searchTerm]);

  // Filter jobs by search term and status (supports job name, contractor, location, Job ID, and Instance ID)
  const filteredJobs = jobs?.filter(instance => {
    // Status filter
    if (selectedStatus) {
      const instanceStatus = instance?.status || instance?.timesheet?.[0]?.status;
      if (instanceStatus !== selectedStatus) return false;
    }

    // Search filter
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase().trim();
    const job = instance?.job;

    // Search by job name, contractor name, or location
    if (
      job?.job_name?.toLowerCase()?.includes(search) ||
      instance?.contractor?.full_name?.toLowerCase()?.includes(search) ||
      job?.location_address?.toLowerCase()?.includes(search)
    ) {
      return true;
    }

    // Search by Job Display ID (e.g., "JOB-ABC123")
    const jobDisplayId = job?.job_display_id || `JOB-${String(job?.id || '').slice(-6).toUpperCase()}`;
    if (jobDisplayId?.toLowerCase()?.includes(search)) {
      return true;
    }

    // Search by raw Job ID (UUID or numeric)
    if (job?.id && String(job.id).toLowerCase().includes(search)) {
      return true;
    }

    // Search by Instance ID or Instance Number
    if (instance?.id && String(instance.id).toLowerCase().includes(search)) {
      return true;
    }
    if (instance?.instance_number && String(instance.instance_number).includes(search)) {
      return true;
    }

    // Search by combined Job + Instance format (e.g., "JOB-ABC123-1" or "JOB-ABC123#1")
    const instanceNum = instance?.instance_number || 1;
    const combinedId1 = `${jobDisplayId}-${instanceNum}`.toLowerCase();
    const combinedId2 = `${jobDisplayId}#${instanceNum}`.toLowerCase();
    if (combinedId1.includes(search) || combinedId2.includes(search)) {
      return true;
    }

    // Search by Transaction ID (if this instance has a payment batch)
    // This enables searching by bank transfer reference
    if (transactionSearchResults.has(instance?.id)) {
      return true;
    }

    return false;
  });

  // Group instances by hierarchy (Agency → Service Receiver → Job → Instances)
  const hierarchyGroups = useMemo(() => {
    return groupInstancesByHierarchy(filteredJobs);
  }, [filteredJobs]);

  // Pre-calculate financials for all instances
  const instanceFinancials = useMemo(() => {
    const financialsMap = new Map();
    filteredJobs?.forEach(instance => {
      const financials = calculateInstanceFinancials(instance, instance?.job, companyGstRate);
      financialsMap.set(instance?.id, financials);
    });
    return financialsMap;
  }, [filteredJobs, companyGstRate]);

  // Check if any jobs have partner agency (for Commission column)
  const hasPartnerAgencyJobs = useMemo(() => {
    return filteredJobs?.some(instance => instance?.job?.job_source === 'partner_agency');
  }, [filteredJobs]);

  // Calculate financial summary for header card
  const financialSummary = useMemo(() => {
    // Determine which instances to calculate
    const instancesToCalculate = selectedInstances.size > 0
      ? filteredJobs?.filter(inst => selectedInstances.has(inst?.id)) || []
      : filteredJobs || [];

    // Calculate financials for each instance
    const financials = instancesToCalculate.map(instance =>
      calculateInstanceFinancials(instance, instance?.job, companyGstRate)
    );

    // Aggregate totals
    const aggregated = aggregateFinancials(financials);

    // Calculate gross revenue (client charges including GST)
    const grossRevenue = (aggregated.chargeToClient || 0) +
      (aggregated.hasAnyOvertimeApproved ? (aggregated.overtimeChargeToClient || 0) : 0);

    // Calculate GST collected from clients
    const gstCollected = (aggregated.gstOnCharge || 0) +
      (aggregated.hasAnyOvertimeApproved ? (aggregated.gstOnOvertimeCharge || 0) : 0);

    // Calculate total payouts to contractors
    const totalPayouts = (aggregated.payoutToContractor || 0) +
      (aggregated.hasAnyOvertimeApproved ? (aggregated.overtimePayoutToContractor || 0) : 0);

    // Calculate GST paid to contractors
    const gstPaid = (aggregated.gstOnPayout || 0) +
      (aggregated.hasAnyOvertimeApproved ? (aggregated.gstOnOvertimePayout || 0) : 0);

    // Calculate net profit
    const netProfit = (aggregated.profit || 0) +
      (aggregated.hasAnyOvertimeApproved ? (aggregated.overtimeProfit || 0) : 0);

    // Calculate profit margin (based on revenue excluding GST)
    const revenueExclGst = grossRevenue - gstCollected;
    const profitMargin = revenueExclGst > 0 ? (netProfit / revenueExclGst) * 100 : 0;

    // Determine context label
    let contextLabel = 'All Time';
    if (selectedInstances.size > 0) {
      contextLabel = `${selectedInstances.size} Selected`;
    } else if (selectedPeriod !== 'all') {
      const periodOption = PERIOD_OPTIONS.find(p => p.value === selectedPeriod);
      contextLabel = periodOption?.label || selectedPeriod;
    }

    return {
      grossRevenue,
      gstCollected,
      totalPayouts,
      gstPaid,
      netProfit,
      profitMargin,
      contextLabel,
      instanceCount: instancesToCalculate.length
    };
  }, [filteredJobs, selectedInstances, companyGstRate, selectedPeriod]);

  // Calculate selected approved instances for bulk payment
  const selectedApprovedForPayment = useMemo(() => {
    if (selectedInstances.size === 0) return { instances: [], total: 0, count: 0 };

    const approvedInstances = filteredJobs?.filter(inst =>
      selectedInstances.has(inst?.id) && inst?.status === 'approved'
    ) || [];

    // Calculate total payout amount for these instances
    const total = approvedInstances.reduce((sum, instance) => {
      const financials = calculateInstanceFinancials(instance, instance?.job, companyGstRate);
      // Total payout = base payout + approved overtime payout
      const basePayout = financials.payoutToContractor || 0;
      const overtimePayout = financials.hasOvertimeApproved ? (financials.overtimePayoutToContractor || 0) : 0;
      return sum + basePayout + overtimePayout;
    }, 0);

    return {
      instances: approvedInstances,
      instanceIds: approvedInstances.map(i => i.id),
      total,
      count: approvedInstances.length
    };
  }, [filteredJobs, selectedInstances, companyGstRate]);

  // Toggle client expansion
  const toggleClient = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  // Toggle job site expansion
  const toggleJobSite = (jobSiteKey) => {
    setExpandedJobSites(prev => {
      const next = new Set(prev);
      if (next.has(jobSiteKey)) {
        next.delete(jobSiteKey);
      } else {
        next.add(jobSiteKey);
      }
      return next;
    });
  };

  // Toggle job expansion (for instances)
  const toggleJob = (jobId) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  // Action handlers
  const handleApprove = async (instanceId) => {
    try {
      await reportsEarningsService.approveReport(instanceId);
      loadData();
    } catch (err) {
      console.error('Error approving report:', err);
      setError(err?.message || 'Failed to approve report');
    }
  };

  const handleReject = async (instanceId, reason) => {
    try {
      await reportsEarningsService.rejectReport(instanceId, reason);
      setRejectingInstance(null);
      loadData();
    } catch (err) {
      console.error('Error rejecting report:', err);
      setError(err?.message || 'Failed to reject report');
    }
  };

  const handleDispute = async (instanceId, reason, category) => {
    try {
      await reportsEarningsService.disputeReport(instanceId, reason, category);
      setDisputingInstance(null);
      loadData();
    } catch (err) {
      console.error('Error creating dispute:', err);
      setError(err?.message || 'Failed to create dispute');
    }
  };

  const handleMarkAsUnpaid = async (instanceId) => {
    if (!instanceId) {
      console.error('No instance ID provided to handleMarkAsUnpaid');
      setError('No instance ID provided');
      return;
    }

    // Optimistic update - update both status and nested timesheet status
    setJobs(prev => prev.map(job => {
      if (job.id !== instanceId) return job;
      const updated = { ...job, status: 'approved' };
      // Also update nested timesheet status if it exists
      if (job.timesheet?.[0]) {
        updated.timesheet = [{ ...job.timesheet[0], status: 'approved' }];
      }
      return updated;
    }));

    // Skip realtime refreshes for 3 seconds since we already updated optimistically
    skipRealtimeUntil.current = Date.now() + 3000;

    try {
      await reportsEarningsService.markAsUnpaid(instanceId);
    } catch (err) {
      // Revert on error
      setJobs(prev => prev.map(job => {
        if (job.id !== instanceId) return job;
        const reverted = { ...job, status: 'paid' };
        if (job.timesheet?.[0]) {
          reverted.timesheet = [{ ...job.timesheet[0], status: 'paid' }];
        }
        return reverted;
      }));
      console.error('Error marking as unpaid:', err);
      setError(err?.message || 'Failed to mark as unpaid');
    }
  };

  // AR (Client Payment) handlers - with optimistic updates
  const handleMarkAsCollected = async (instanceId) => {
    // Optimistic update
    setJobs(prev => prev.map(job =>
      job.id === instanceId
        ? { ...job, client_payment_received: true }
        : job
    ));

    // Skip realtime refreshes for 3 seconds since we already updated optimistically
    skipRealtimeUntil.current = Date.now() + 3000;

    try {
      await reportsEarningsService.markAsCollected(instanceId);
    } catch (err) {
      // Revert on error
      setJobs(prev => prev.map(job =>
        job.id === instanceId
          ? { ...job, client_payment_received: false }
          : job
      ));
      console.error('Error marking as collected:', err);
      setError(err?.message || 'Failed to mark as collected');
    }
  };

  const handleMarkAsOutstanding = async (instanceId) => {
    // Optimistic update
    setJobs(prev => prev.map(job =>
      job.id === instanceId
        ? { ...job, client_payment_received: false }
        : job
    ));

    // Skip realtime refreshes for 3 seconds since we already updated optimistically
    skipRealtimeUntil.current = Date.now() + 3000;

    try {
      await reportsEarningsService.markAsOutstanding(instanceId);
    } catch (err) {
      // Revert on error
      setJobs(prev => prev.map(job =>
        job.id === instanceId
          ? { ...job, client_payment_received: true }
          : job
      ));
      console.error('Error marking as outstanding:', err);
      setError(err?.message || 'Failed to mark as outstanding');
    }
  };

  // Charge adjustment handlers
  const handleChargeAdjustment = async (amount, reason, includeGst = false, gstAmount = 0) => {
    if (!adjustingChargeInstance) return;
    setChargeSubmitting(true);
    try {
      await reportsEarningsService.applyChargeAdjustment(adjustingChargeInstance.id, amount, reason, includeGst, gstAmount);
      setAdjustingChargeInstance(null);
      loadData();
    } catch (err) {
      console.error('Error applying charge adjustment:', err);
      setError(err?.message || 'Failed to apply charge adjustment');
    } finally {
      setChargeSubmitting(false);
    }
  };

  const handleAddCharge = async (chargeData) => {
    if (!addingChargesInstance) return;
    try {
      await reportsEarningsService.addAdditionalCharge(addingChargesInstance.id, chargeData);
      loadData();
    } catch (err) {
      console.error('Error adding charge:', err);
      setError(err?.message || 'Failed to add charge');
    }
  };

  const handleUpdateCharge = async (chargeId, chargeData) => {
    try {
      await reportsEarningsService.updateAdditionalCharge(chargeId, chargeData);
      loadData();
    } catch (err) {
      console.error('Error updating charge:', err);
      setError(err?.message || 'Failed to update charge');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    try {
      await reportsEarningsService.deleteAdditionalCharge(chargeId);
      loadData();
    } catch (err) {
      console.error('Error deleting charge:', err);
      setError(err?.message || 'Failed to delete charge');
    }
  };

  // Overtime handlers
  const handleOvertimeApprove = async (approvedAmount) => {
    if (!reviewingOvertime) return;
    try {
      setOvertimeProcessing(true);
      await overtimeService.approveOvertimeRequest(reviewingOvertime.id, approvedAmount);
      setReviewingOvertime(null);
      loadData();
    } catch (err) {
      console.error('Error approving overtime:', err);
      setError(err?.message || 'Failed to approve overtime request');
    } finally {
      setOvertimeProcessing(false);
    }
  };

  const handleOvertimeReject = async (reason) => {
    if (!reviewingOvertime) return;
    try {
      setOvertimeProcessing(true);
      await overtimeService.rejectOvertimeRequest(reviewingOvertime.id, reason);
      setReviewingOvertime(null);
      loadData();
    } catch (err) {
      console.error('Error rejecting overtime:', err);
      setError(err?.message || 'Failed to reject overtime request');
    } finally {
      setOvertimeProcessing(false);
    }
  };

  // Handle successful payment recording (both single and bulk)
  const handleBulkPaymentSuccess = (result) => {
    setShowRecordPaymentModal(false);

    // Determine which instances were paid
    const paidInstanceIds = singlePaymentInstance
      ? new Set([singlePaymentInstance.id])
      : new Set(selectedApprovedForPayment.instanceIds);

    // Clear selection and single payment instance
    setSelectedInstances(new Set());
    setSinglePaymentInstance(null);

    // Optimistic update - mark all paid instances
    setJobs(prev => prev.map(job => {
      if (!paidInstanceIds.has(job.id)) return job;
      const updated = { ...job, status: 'paid' };
      if (job.timesheet?.[0]) {
        updated.timesheet = [{ ...job.timesheet[0], status: 'paid', payment_batch_id: result.batch?.id }];
      }
      return updated;
    }));

    // Skip realtime refreshes for 3 seconds
    skipRealtimeUntil.current = Date.now() + 3000;

    // Refresh data after a short delay to ensure database is updated
    setTimeout(() => {
      loadData();
    }, 1000);
  };

  // Tab counts
  const getTabCount = (tabKey) => {
    if (tabKey === 'all') return statusCounts.all || 0;
    if (tabKey === 'overtime') return overtimeCount;
    return statusCounts[tabKey] || 0;
  };

  // Permission check
  if (!canViewEarnings) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 overflow-y-auto pt-16 pl-0 lg:pl-60">
            <AccessDenied
              title="Earnings Access Restricted"
              message="You don't have permission to view earnings data. Please contact your administrator."
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 overflow-y-auto pt-16 pl-0 lg:pl-60">
          <div className="container mx-auto px-6 py-8">
            {/* Page Header with Financial Summary Card */}
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4">
                {/* Financial Summary Card */}
                <div className="flex-1">
                  <FinancialSummaryCard
                    grossRevenue={financialSummary.grossRevenue}
                    gstCollected={financialSummary.gstCollected}
                    totalPayouts={financialSummary.totalPayouts}
                    gstPaid={financialSummary.gstPaid}
                    netProfit={financialSummary.netProfit}
                    profitMargin={financialSummary.profitMargin}
                    contextLabel={financialSummary.contextLabel}
                    instanceCount={financialSummary.instanceCount}
                    numberFormat={preferences.number_format}
                  />
                </div>
                {/* Right side: Buttons + Search */}
                <div className="flex flex-col gap-3 flex-shrink-0">
                  {/* Buttons Row */}
                  <div className="flex items-center gap-3">
                    {/* Bulk Pay Button - Always visible, disabled when no approved items selected */}
                    <button
                      onClick={() => selectedApprovedForPayment.count > 0 && setShowRecordPaymentModal(true)}
                      disabled={selectedApprovedForPayment.count === 0}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                        selectedApprovedForPayment.count > 0
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-green-600/30 text-green-400/50 cursor-not-allowed"
                      )}
                      title={selectedApprovedForPayment.count === 0 ? "Select approved jobs to mark as paid" : `Mark ${selectedApprovedForPayment.count} job(s) as paid`}
                    >
                      <DollarSign size={18} />
                      {selectedApprovedForPayment.count > 0
                        ? `Mark ${selectedApprovedForPayment.count} as Paid`
                        : "Select Jobs to Pay"}
                    </button>
                    {/* Export CSV Button */}
                    {activeTab !== 'overtime' && (
                      <button
                        onClick={() => exportReportToCSV(filteredJobs, getDateFilename('earnings-report'))}
                        disabled={!filteredJobs?.length}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          filteredJobs?.length > 0
                            ? 'bg-accent hover:bg-accent-600 text-white'
                            : 'bg-border/50 text-text-muted cursor-not-allowed'
                        }`}
                      >
                        <Download size={18} />
                        Export CSV
                      </button>
                    )}
                    <button
                      onClick={loadData}
                      className="flex items-center gap-2 px-4 py-2 bg-border hover:bg-border-light text-white rounded-lg transition-colors"
                    >
                      <RefreshCw size={18} />
                      Refresh
                    </button>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                        showFilters
                          ? "bg-primary text-white"
                          : "bg-border hover:bg-border-light text-white"
                      )}
                    >
                      <SlidersHorizontal size={18} />
                      Filters
                      {(selectedContractor || selectedClient || selectedJobSite || selectedPeriod !== 'all' || selectedStatus) && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary rounded-full">
                          {[selectedContractor, selectedClient, selectedJobSite, selectedPeriod !== 'all' ? selectedPeriod : '', selectedStatus].filter(Boolean).length}
                        </span>
                      )}
                    </button>
                  </div>
                  {/* Search Bar - Below buttons, same width */}
                  <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search job name, contractor, ID, or Transaction..."
                      className="w-full pl-10 pr-10 py-2 bg-border border border-border-light rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-text-secondary hover:text-red-400 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters Floating Popup */}
              {showFilters && (
                <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4">
                  {/* Backdrop */}
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setShowFilters(false)}
                  />

                  {/* Popup */}
                  <div className="relative w-full max-w-2xl bg-surface border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface-dark">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                          <SlidersHorizontal size={16} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-text-primary">Filters</h3>
                          <p className="text-xs text-text-secondary">Refine your earnings view</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    {/* Filter Content */}
                    <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
                      {/* Row 1: Period & Status */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Period Filter */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-text-secondary mb-2">
                            <Calendar size={14} />
                            Time Period
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {PERIOD_OPTIONS.map(option => (
                              <button
                                key={option.value}
                                onClick={() => setSelectedPeriod(option.value)}
                                className={cn(
                                  'px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                                  selectedPeriod === option.value
                                    ? 'bg-primary border-primary text-white'
                                    : 'bg-background border-border text-text-secondary hover:border-primary hover:text-text-primary'
                                )}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                          {/* Custom Date Range */}
                          {selectedPeriod === 'custom' && (
                            <div className="flex items-center gap-3 mt-3">
                              <input
                                type="date"
                                value={customDateFrom}
                                onChange={(e) => setCustomDateFrom(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                              />
                              <span className="text-text-secondary text-sm">to</span>
                              <input
                                type="date"
                                value={customDateTo}
                                onChange={(e) => setCustomDateTo(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                              />
                            </div>
                          )}
                        </div>

                        {/* Status Filter */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-text-secondary mb-2">
                            <Check size={14} />
                            Payment Status
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: '', label: 'All', color: 'bg-gray-500/20 text-gray-300' },
                              { value: 'submitted', label: 'Pending', color: 'bg-orange-500/20 text-orange-300' },
                              { value: 'approved', label: 'Approved', color: 'bg-green-500/20 text-green-300' },
                              { value: 'paid', label: 'Paid', color: 'bg-purple-500/20 text-purple-300' },
                              { value: 'disputed', label: 'Disputed', color: 'bg-red-500/20 text-red-300' }
                            ].map(status => (
                              <button
                                key={status.value}
                                onClick={() => setSelectedStatus(status.value)}
                                className={cn(
                                  'px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                                  selectedStatus === status.value
                                    ? `${status.color} border-current`
                                    : 'bg-background border-border text-text-secondary hover:border-border-light'
                                )}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-border" />

                      {/* Row 2: Entity Filters */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {/* Contractor Filter */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-text-secondary mb-2">
                            <Users size={14} />
                            Contractor
                          </label>
                          <div className="relative">
                            <select
                              value={selectedContractor}
                              onChange={(e) => setSelectedContractor(e.target.value)}
                              className="w-full px-3 py-2 pr-8 text-sm bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary appearance-none cursor-pointer"
                            >
                              <option value="">All Contractors</option>
                              {contractors?.map(c => (
                                <option key={c.id} value={c.id}>{c.full_name}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                          </div>
                        </div>

                        {/* Client Filter */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-text-secondary mb-2">
                            <Building size={14} />
                            Client
                          </label>
                          <div className="relative">
                            <select
                              value={selectedClient}
                              onChange={(e) => setSelectedClient(e.target.value)}
                              className="w-full px-3 py-2 pr-8 text-sm bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary appearance-none cursor-pointer"
                            >
                              <option value="">All Clients</option>
                              {clients?.map(c => (
                                <option key={c.id} value={c.id}>{c.client_name}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                          </div>
                        </div>

                        {/* Job Site Filter */}
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-text-secondary mb-2">
                            <Building2 size={14} />
                            Job Site
                          </label>
                          <div className="relative">
                            <select
                              value={selectedJobSite}
                              onChange={(e) => setSelectedJobSite(e.target.value)}
                              className="w-full px-3 py-2 pr-8 text-sm bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary appearance-none cursor-pointer"
                            >
                              <option value="">All Job Sites</option>
                              {jobSites?.map(s => (
                                <option key={s.id} value={s.id}>{s.site_name}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between px-5 py-4 border-t border-border bg-surface-dark">
                      <button
                        onClick={() => {
                          setSelectedContractor('');
                          setSelectedClient('');
                          setSelectedJobSite('');
                          setSelectedPeriod('all');
                          setSelectedStatus('');
                          setCustomDateFrom('');
                          setCustomDateTo('');
                        }}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <X size={16} />
                        Clear All
                      </button>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <Check size={16} />
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error State */}
            {error && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-accent animate-spin" />
              </div>
            )}

            {/* Empty State - Regular tabs */}
            {!loading && activeTab !== 'overtime' && filteredJobs?.length === 0 && (
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-text-primary mb-2">No earnings found</h3>
                <p className="text-text-secondary">
                  {activeTab === 'all'
                    ? 'No submitted earnings yet. Earnings will appear here once contractors submit their work.'
                    : `No earnings with "${TABS.find(t => t.key === activeTab)?.label}" status.`}
                </p>
              </div>
            )}

            {/* Empty State - Overtime tab */}
            {!loading && activeTab === 'overtime' && overtimeRequests?.length === 0 && (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-xl font-medium text-text-primary mb-2">No overtime requests</h3>
                <p className="text-text-secondary">
                  Overtime requests from contractors will appear here when they work beyond estimated hours.
                </p>
              </div>
            )}

            {/* Overtime Requests List */}
            {!loading && activeTab === 'overtime' && overtimeRequests?.length > 0 && (
              <div className="space-y-4">
                {overtimeRequests.map(request => (
                  <div
                    key={request.id}
                    className="bg-surface border border-border rounded-lg p-4 hover:border-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-text-primary">
                            {request?.job_instance?.jobs?.job_name || 'Unknown Job'}
                          </h3>
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                            {request.overtime_hours}h overtime
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-text-tertiary text-xs">Contractor</p>
                            <p className="text-text-secondary">{request?.contractor?.full_name || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-text-tertiary text-xs">Hours (Est → Actual)</p>
                            <p className="text-text-secondary">
                              {request.estimated_hours}h → {request.actual_hours}h
                            </p>
                          </div>
                          <div>
                            <p className="text-text-tertiary text-xs">Base Pay</p>
                            <p className="text-text-secondary">${parseFloat(request.base_pay).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-text-tertiary text-xs">Overtime Requested</p>
                            <p className="text-orange-400 font-medium">
                              +${parseFloat(request.requested_overtime_amount).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-background rounded-lg">
                          <p className="text-text-tertiary text-xs mb-1">Justification</p>
                          <p className="text-text-secondary text-sm">{request.justification}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setReviewingOvertime(request)}
                        className="ml-4 px-4 py-2 bg-accent hover:bg-accent-600 text-white rounded-lg transition-colors whitespace-nowrap"
                      >
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Job Hierarchy Table - Agency → Service Receiver → Job → Instance */}
            {!loading && activeTab !== 'overtime' && filteredJobs?.length > 0 && (
              <div className="bg-surface border border-border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[1400px]">
                  <thead>
                    <tr className="bg-surface-dark border-b border-border">
                      <th className="px-2 py-3 text-left w-8">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={filteredJobs?.length > 0 && selectedInstances.size === filteredJobs?.length}
                            onChange={() => {
                              if (selectedInstances.size === filteredJobs?.length) {
                                setSelectedInstances(new Set());
                              } else {
                                setSelectedInstances(new Set(filteredJobs?.map(i => i?.id)));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                          />
                        </label>
                      </th>
                      <th className="px-3 py-3 text-left text-sm font-semibold text-text-secondary whitespace-nowrap">Job / Instance | Contractor</th>
                      <th className="px-3 py-3 text-left text-sm font-semibold text-text-secondary whitespace-nowrap">Status</th>
                      <th className="px-3 py-3 text-left text-sm font-semibold text-text-secondary whitespace-nowrap">Date</th>
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">$ Charge</th>
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">GST (C)</th>
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">OT Charge</th>
                      {hasPartnerAgencyJobs && (
                        <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">Commission</th>
                      )}
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">$ Payout</th>
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">GST (P)</th>
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">OT Payout</th>
                      {showProfitColumn && (
                        <th className="px-3 py-3 text-right text-sm font-semibold text-accent whitespace-nowrap">Profit</th>
                      )}
                      <th className="px-3 py-3 text-right text-sm font-semibold text-text-secondary whitespace-nowrap">Actions</th>
                    </tr>
                    {/* Gradient separator after header */}
                    <tr>
                      <td colSpan={hasPartnerAgencyJobs && showProfitColumn ? 13 : hasPartnerAgencyJobs || showProfitColumn ? 12 : 11} className="p-0">
                        <div className="h-1 bg-gradient-to-r from-[#FF7A00] via-[#00C47D] to-[#FF7A00]" />
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(hierarchyGroups.entries()).map(([clientId, { client, jobSites }], clientIdx) => {
                      const isClientExpanded = expandedClients.has(clientId);
                      // Count total instances and aggregate financials for this client
                      let totalClientInstances = 0;
                      const clientFinancialsArray = [];
                      jobSites.forEach(({ jobs }) => {
                        jobs.forEach(({ instances }) => {
                          totalClientInstances += instances.length;
                          instances.forEach(inst => {
                            const f = instanceFinancials.get(inst?.id);
                            if (f) clientFinancialsArray.push(f);
                          });
                        });
                      });
                      const clientTotals = aggregateFinancials(clientFinancialsArray);

                      // Get all instance IDs for this client for selection
                      const allClientInstanceIds = [];
                      jobSites.forEach(({ jobs }) => {
                        jobs.forEach(({ instances }) => {
                          instances.forEach(inst => allClientInstanceIds.push(inst?.id));
                        });
                      });
                      const allClientInstancesSelected = allClientInstanceIds.length > 0 && allClientInstanceIds.every(id => selectedInstances.has(id));
                      const someClientInstancesSelected = allClientInstanceIds.some(id => selectedInstances.has(id));

                      return (
                        <React.Fragment key={clientId}>
                          {/* Client Separator - subtle divider between clients */}
                          {clientIdx > 0 && (
                            <tr className="h-2 bg-background">
                              <td colSpan={hasPartnerAgencyJobs && showProfitColumn ? 13 : hasPartnerAgencyJobs || showProfitColumn ? 12 : 11} className="p-0">
                                <div className="h-px bg-border" />
                              </td>
                            </tr>
                          )}
                          {/* Client Row - Top Level */}
                          <tr
                            className={cn(
                              'bg-surface-dark cursor-pointer hover:brightness-110 transition-all',
                              client.isUnassigned ? 'border-l-4 border-l-gray-500' : 'border-l-4 border-l-primary-orange'
                            )}
                            onClick={() => toggleClient(clientId)}
                          >
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={allClientInstancesSelected}
                                ref={el => {
                                  if (el) el.indeterminate = someClientInstancesSelected && !allClientInstancesSelected;
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedInstances(prev => {
                                    const next = new Set(prev);
                                    if (allClientInstancesSelected) {
                                      allClientInstanceIds.forEach(id => next.delete(id));
                                    } else {
                                      allClientInstanceIds.forEach(id => next.add(id));
                                    }
                                    return next;
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                              />
                            </td>
                            <td className="px-3 py-4">
                              <div className="flex items-start gap-2">
                                {isClientExpanded ? (
                                  <ChevronDown className="w-5 h-5 text-accent mt-0.5" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-text-secondary mt-0.5" />
                                )}
                                <Building className="w-5 h-5 text-primary-orange mt-0.5" />
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-text-primary">
                                      {client.name}
                                    </span>
                                    {client.tag && (
                                      <span className="px-1.5 py-0.5 bg-accent/10 text-accent text-xs rounded font-medium">
                                        {client.tag}
                                      </span>
                                    )}
                                    <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                      {totalClientInstances}
                                    </span>
                                  </div>
                                  {client.contactPerson && (
                                    <span className="text-xs text-text-tertiary mt-0.5">
                                      {client.contactPerson}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3"></td>
                            <td className="px-3 py-3"></td>
                            <td className="px-3 py-3 text-right text-sm font-semibold text-text-primary">
                              {formatCurrency(clientTotals.chargeToClient)}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-text-secondary">
                              {formatCurrency(clientTotals.gstOnCharge, false)}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-text-secondary">
                              {clientTotals.hasAnyOvertime ? formatCurrency(clientTotals.overtimeChargeToClient, false) : '-'}
                            </td>
                            {hasPartnerAgencyJobs && (
                              <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                {clientTotals.hasAnyCommission ? formatCurrency(clientTotals.commissionMargin, false) : '-'}
                              </td>
                            )}
                            <td className="px-3 py-3 text-right text-sm font-semibold text-text-primary">
                              {formatCurrency(clientTotals.payoutToContractor)}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-text-secondary">
                              {formatCurrency(clientTotals.gstOnPayout, false)}
                            </td>
                            <td className="px-3 py-3 text-right text-xs text-text-secondary">
                              {clientTotals.hasAnyOvertime ? formatCurrency(clientTotals.overtimePayoutToContractor, false) : '-'}
                            </td>
                            {showProfitColumn && (
                              <td className={`px-3 py-3 text-right text-sm font-semibold ${(clientTotals.profit || 0) < 0 ? 'text-red-500' : 'text-accent'}`}>
                                {formatCurrency(clientTotals.profit)}
                              </td>
                            )}
                            <td className="px-3 py-3"></td>
                          </tr>

                          {/* Job Sites within this Client */}
                          {isClientExpanded && Array.from(jobSites.entries()).map(([jobSiteId, { jobSite, jobs: siteJobs }], siteIdx) => {
                            const isOdd = siteIdx % 2 === 0;
                            const colors = isOdd ? JOBSITE_COLORS.odd : JOBSITE_COLORS.even;
                            const jobSiteKey = `${clientId}-${jobSiteId}`;
                            const isJobSiteExpanded = expandedJobSites.has(jobSiteKey);

                            // Count total instances and aggregate financials for this job site
                            let totalSiteInstances = 0;
                            const siteFinancialsArray = [];
                            siteJobs.forEach(({ instances }) => {
                              totalSiteInstances += instances.length;
                              instances.forEach(inst => {
                                const f = instanceFinancials.get(inst?.id);
                                if (f) siteFinancialsArray.push(f);
                              });
                            });
                            const siteTotals = aggregateFinancials(siteFinancialsArray);

                            // Get all instance IDs in this job site for selection
                            const allSiteInstanceIds = [];
                            siteJobs.forEach(({ instances }) => {
                              instances.forEach(inst => allSiteInstanceIds.push(inst?.id));
                            });
                            const allSiteInstancesSelected = allSiteInstanceIds.length > 0 && allSiteInstanceIds.every(id => selectedInstances.has(id));
                            const someSiteInstancesSelected = allSiteInstanceIds.some(id => selectedInstances.has(id));

                            return (
                              <React.Fragment key={jobSiteKey}>
                                {/* Job Site Row */}
                                <tr
                                  className={cn(
                                    colors.jobSite,
                                    'cursor-pointer hover:brightness-110 transition-all'
                                  )}
                                  onClick={() => toggleJobSite(jobSiteKey)}
                                >
                                  <td className="px-3 py-3 pl-6">
                                    <input
                                      type="checkbox"
                                      checked={allSiteInstancesSelected}
                                      ref={el => {
                                        if (el) el.indeterminate = someSiteInstancesSelected && !allSiteInstancesSelected;
                                      }}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setSelectedInstances(prev => {
                                          const next = new Set(prev);
                                          if (allSiteInstancesSelected) {
                                            allSiteInstanceIds.forEach(id => next.delete(id));
                                          } else {
                                            allSiteInstanceIds.forEach(id => next.add(id));
                                          }
                                          return next;
                                        });
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                    />
                                  </td>
                                  <td className="px-3 py-3.5">
                                    <div className="flex items-start gap-2 pl-4">
                                      {isJobSiteExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-accent mt-0.5" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-text-secondary mt-0.5" />
                                      )}
                                      <Building2 className="w-4 h-4 text-accent mt-0.5" />
                                      <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-text-primary truncate max-w-[180px]">
                                            {jobSite.name}
                                          </span>
                                          <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                            {totalSiteInstances}
                                          </span>
                                        </div>
                                        {jobSite.address && (
                                          <span className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5 truncate max-w-[250px]">
                                            <MapPin className="w-3 h-3 flex-shrink-0" />
                                            {jobSite.address}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3"></td>
                                  <td className="px-3 py-3"></td>
                                  <td className="px-3 py-3 text-right text-sm text-text-primary">
                                    {formatCurrency(siteTotals.chargeToClient)}
                                  </td>
                                  <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                    {formatCurrency(siteTotals.gstOnCharge, false)}
                                  </td>
                                  <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                    {siteTotals.hasAnyOvertime ? formatCurrency(siteTotals.overtimeChargeToClient, false) : '-'}
                                  </td>
                                  {hasPartnerAgencyJobs && (
                                    <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                      {siteTotals.hasAnyCommission ? formatCurrency(siteTotals.commissionMargin, false) : '-'}
                                    </td>
                                  )}
                                  <td className="px-3 py-3 text-right text-sm text-text-primary">
                                    {formatCurrency(siteTotals.payoutToContractor)}
                                  </td>
                                  <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                    {formatCurrency(siteTotals.gstOnPayout, false)}
                                  </td>
                                  <td className="px-3 py-3 text-right text-xs text-text-secondary">
                                    {siteTotals.hasAnyOvertime ? formatCurrency(siteTotals.overtimePayoutToContractor, false) : '-'}
                                  </td>
                                  {showProfitColumn && (
                                    <td className={`px-3 py-3 text-right text-sm ${(siteTotals.profit || 0) < 0 ? 'text-red-500' : 'text-accent'}`}>
                                      {formatCurrency(siteTotals.profit)}
                                    </td>
                                  )}
                                  <td className="px-3 py-3"></td>
                                </tr>

                                {/* Job Rows (when job site is expanded) */}
                                {isJobSiteExpanded && Array.from(siteJobs.entries()).map(([jobId, { job, instances: jobInstances }], jobIndex) => {
                                  const isJobExpanded = expandedJobs.has(jobId);
                                  const isLastJob = jobIndex === siteJobs.size - 1;
                                  const showJobDropdown = jobInstances.length > 1;

                                  // Get all instance IDs in this job for selection
                                  const jobInstanceIds = jobInstances.map(inst => inst?.id);
                                  const allJobInstancesSelected = jobInstanceIds.length > 0 && jobInstanceIds.every(id => selectedInstances.has(id));
                                  const someJobInstancesSelected = jobInstanceIds.some(id => selectedInstances.has(id));

                                  // Calculate financials for this job (aggregate if multiple instances)
                                  const jobFinancialsArray = jobInstances.map(inst => instanceFinancials.get(inst?.id)).filter(Boolean);
                                  const jobTotals = aggregateFinancials(jobFinancialsArray);

                                  // For single instance, get the direct financials
                                  const singleInstanceFinancials = !showJobDropdown ? instanceFinancials.get(jobInstances[0]?.id) : null;

                                  return (
                                    <React.Fragment key={jobId}>
                                      {/* Job Row */}
                                      <tr
                                        className={cn(
                                          colors.job,
                                          !isLastJob && 'border-b border-dashed border-accent/40',
                                          showJobDropdown && 'cursor-pointer hover:brightness-110',
                                          'transition-all'
                                        )}
                                        onClick={() => showJobDropdown && toggleJob(jobId)}
                                      >
                                        {/* Checkbox */}
                                        <td className="px-3 py-1.5 pl-10">
                                          <input
                                            type="checkbox"
                                            checked={allJobInstancesSelected}
                                            ref={(el) => { if (el) el.indeterminate = someJobInstancesSelected && !allJobInstancesSelected; }}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              setSelectedInstances(prev => {
                                                const next = new Set(prev);
                                                if (allJobInstancesSelected) {
                                                  jobInstanceIds.forEach(id => next.delete(id));
                                                } else {
                                                  jobInstanceIds.forEach(id => next.add(id));
                                                }
                                                return next;
                                              });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                          />
                                        </td>
                                        {/* Job Name with instance count */}
                                        <td className="px-3 py-1.5">
                                          <div className="flex items-center gap-2 pl-8">
                                            {showJobDropdown && (
                                              isJobExpanded ? (
                                                <ChevronDown className="w-3 h-3 text-accent" />
                                              ) : (
                                                <ChevronRight className="w-3 h-3 text-text-secondary" />
                                              )
                                            )}
                                            <span className="font-medium text-text-primary text-sm truncate max-w-[150px]">{job?.job_name}</span>
                                            {showJobDropdown ? (
                                              <span className="text-xs text-text-secondary bg-border px-2 py-0.5 rounded">
                                                {jobInstances.length}
                                              </span>
                                            ) : (
                                              <>
                                                <span className="text-xs text-text-tertiary mx-1">|</span>
                                                <span className="text-xs text-text-tertiary truncate max-w-[100px]">
                                                  {jobInstances[0]?.contractor?.full_name}
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                        {/* Status */}
                                        <td className="px-3 py-1.5">
                                          {!showJobDropdown ? (
                                            <span className={cn(
                                              'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                              getTimesheetStatusColor(jobInstances[0]?.status)
                                            )}>
                                              {formatStatusText(jobInstances[0]?.status)}
                                            </span>
                                          ) : null}
                                        </td>
                                        {/* Date */}
                                        <td className="px-3 py-1.5 text-xs text-text-secondary">
                                          {!showJobDropdown && formatDate(jobInstances[0]?.scheduled_date, preferences?.date_format)}
                                        </td>
                                        {/* $ Charge */}
                                        <td className="px-3 py-1.5 text-right text-sm text-text-primary">
                                          {formatCurrency(jobTotals.chargeToClient)}
                                        </td>
                                        {/* GST (C) */}
                                        <td className="px-3 py-1.5 text-right text-xs text-text-secondary">
                                          {formatCurrency(jobTotals.gstOnCharge, false)}
                                        </td>
                                        {/* OT Charge */}
                                        <td className="px-3 py-1.5 text-right text-xs text-text-secondary">
                                          {showJobDropdown
                                            ? (jobTotals.hasAnyOvertime ? formatCurrency(jobTotals.overtimeChargeToClient, false) : '-')
                                            : (singleInstanceFinancials?.hasOvertime
                                              ? formatOvertime(singleInstanceFinancials.overtimeChargeToClient, singleInstanceFinancials.clientOvertimeHours, singleInstanceFinancials.overtimeApproved, singleInstanceFinancials.overtimePending)
                                              : '-')}
                                        </td>
                                        {/* Commission */}
                                        {hasPartnerAgencyJobs && (
                                          <td className="px-3 py-1.5 text-right text-xs text-text-secondary">
                                            {jobTotals.hasAnyCommission ? formatCurrency(jobTotals.commissionMargin, false) : '-'}
                                          </td>
                                        )}
                                        {/* $ Payout */}
                                        <td className="px-3 py-1.5 text-right text-sm text-text-primary">
                                          {formatCurrency(jobTotals.payoutToContractor)}
                                        </td>
                                        {/* GST (P) */}
                                        <td className="px-3 py-1.5 text-right text-xs text-text-secondary">
                                          {formatCurrency(jobTotals.gstOnPayout, false)}
                                        </td>
                                        {/* OT Payout */}
                                        <td className="px-3 py-1.5 text-right text-xs text-text-secondary">
                                          {showJobDropdown
                                            ? (jobTotals.hasAnyOvertime ? formatCurrency(jobTotals.overtimePayoutToContractor, false) : '-')
                                            : (singleInstanceFinancials?.hasOvertime
                                              ? formatOvertime(singleInstanceFinancials.overtimePayoutToContractor, singleInstanceFinancials.contractorOvertimeHours, singleInstanceFinancials.overtimeApproved, singleInstanceFinancials.overtimePending)
                                              : '-')}
                                        </td>
                                        {/* Profit */}
                                        {showProfitColumn && (
                                          <td className={`px-3 py-1.5 text-right text-sm ${(jobTotals.profit || 0) < 0 ? 'text-red-500' : 'text-accent'}`}>
                                            {formatCurrency(jobTotals.profit)}
                                          </td>
                                        )}
                                        {/* Actions */}
                                        <td className="px-3 py-1.5">
                                          <div className="flex items-center justify-end gap-1">
                                            {!showJobDropdown && (
                                              <>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingReport(jobInstances[0]);
                                                  }}
                                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                                  title="View report"
                                                >
                                                  <Eye className="w-4 h-4 text-text-primary" />
                                                </button>
                                                {(jobInstances[0]?.timesheet?.[0]?.status === 'submitted' || jobInstances[0]?.status === 'submitted') && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(jobInstances[0]?.id);
                                                      }}
                                                      className="p-1 hover:bg-green-500/20 rounded transition-colors"
                                                      title="Approve"
                                                    >
                                                      <Check className="w-4 h-4 text-green-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRejectingInstance(jobInstances[0]);
                                                      }}
                                                      className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                                      title="Reject"
                                                    >
                                                      <XCircle className="w-4 h-4 text-red-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisputingInstance(jobInstances[0]);
                                                      }}
                                                      className="p-1 hover:bg-yellow-500/20 rounded transition-colors"
                                                      title="Dispute"
                                                    >
                                                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                                    </button>
                                                  </>
                                                )}
                                                {/* AP Status - View Proof (when paid) or Mark as Paid (when approved) */}
                                                {(() => {
                                                  const isPaid = jobInstances[0]?.timesheet?.[0]?.status === 'paid' || jobInstances[0]?.status === 'paid';
                                                  const isApproved = jobInstances[0]?.timesheet?.[0]?.status === 'approved' || jobInstances[0]?.status === 'approved';
                                                  if (!isPaid && !isApproved) return null;

                                                  // If paid, show View Proof button instead of toggle
                                                  if (isPaid) {
                                                    return (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setViewingPaymentDetails(jobInstances[0]?.id);
                                                        }}
                                                        className="p-1 rounded transition-colors hover:bg-purple-500/20"
                                                        title="View payment proof"
                                                      >
                                                        <Receipt className="w-4 h-4 text-purple-400" />
                                                      </button>
                                                    );
                                                  }

                                                  // If approved, show Mark as Paid button (opens payment modal)
                                                  return (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSinglePaymentInstance(jobInstances[0]);
                                                        setShowRecordPaymentModal(true);
                                                      }}
                                                      className="p-1 rounded transition-colors hover:bg-purple-500/20"
                                                      title="Mark contractor paid"
                                                    >
                                                      <Wallet className="w-4 h-4 text-purple-400" />
                                                    </button>
                                                  );
                                                })()}
                                                {/* AR Status Toggle - Client Payment */}
                                                {(() => {
                                                  const isPaid = jobInstances[0]?.timesheet?.[0]?.status === 'paid' || jobInstances[0]?.status === 'paid';
                                                  const isApproved = jobInstances[0]?.timesheet?.[0]?.status === 'approved' || jobInstances[0]?.status === 'approved';
                                                  const isCollected = jobInstances[0]?.client_payment_received;
                                                  if (!isPaid && !isApproved) return null;
                                                  return (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isCollected) {
                                                          handleMarkAsOutstanding(jobInstances[0]?.id);
                                                        } else {
                                                          handleMarkAsCollected(jobInstances[0]?.id);
                                                        }
                                                      }}
                                                      className={cn(
                                                        'p-1 rounded transition-colors',
                                                        isCollected ? 'hover:bg-gray-500/20' : 'hover:bg-blue-500/20'
                                                      )}
                                                      title={isCollected ? 'Mark client invoice outstanding' : 'Mark client invoice collected'}
                                                    >
                                                      {isCollected ? (
                                                        <ReceiptText className="w-4 h-4 text-gray-400" />
                                                      ) : (
                                                        <Receipt className="w-4 h-4 text-blue-400" />
                                                      )}
                                                    </button>
                                                  );
                                                })()}
                                                {/* Charge Adjustment */}
                                                {(() => {
                                                  const isApproved = jobInstances[0]?.status === 'approved' || jobInstances[0]?.status === 'paid';
                                                  if (!isApproved) return null;
                                                  return (
                                                    <>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setAdjustingChargeInstance(jobInstances[0]);
                                                        }}
                                                        className="p-1 hover:bg-blue-500/20 rounded transition-colors"
                                                        title="Adjust charge"
                                                      >
                                                        <Settings2 className="w-4 h-4 text-blue-400" />
                                                      </button>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setAddingChargesInstance(jobInstances[0]);
                                                        }}
                                                        className="p-1 hover:bg-green-500/20 rounded transition-colors"
                                                        title="Add charges"
                                                      >
                                                        <Plus className="w-4 h-4 text-green-400" />
                                                      </button>
                                                    </>
                                                  );
                                                })()}
                                              </>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setViewingJobInvoice({ job, instances: jobInstances });
                                              }}
                                              className="p-1 hover:bg-white/10 rounded transition-colors"
                                              title="View invoice"
                                            >
                                              <FileText className="w-4 h-4 text-text-secondary" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>

                                      {/* Instance Rows (when job is expanded and has multiple instances) */}
                                      {isJobExpanded && showJobDropdown && jobInstances.map((instance, instIndex) => {
                                        const status = instance?.status;
                                        const isLastInstance = instIndex === jobInstances.length - 1;
                                        const instFinancials = instanceFinancials.get(instance?.id);

                                        return (
                                          <tr
                                            key={instance?.id}
                                            className={cn(
                                              colors.instance,
                                              !isLastInstance && 'border-b border-dotted border-gray-600/50'
                                            )}
                                          >
                                            {/* Instance checkbox */}
                                            <td className="px-3 py-1.5 pl-12">
                                              <input
                                                type="checkbox"
                                                checked={selectedInstances.has(instance?.id)}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedInstances(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(instance?.id)) {
                                                      next.delete(instance?.id);
                                                    } else {
                                                      next.add(instance?.id);
                                                    }
                                                    return next;
                                                  });
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                                              />
                                            </td>
                                            {/* Instance info */}
                                            <td className="px-3 py-1.5">
                                              <div className="flex items-center gap-1 pl-12">
                                                <span className="text-xs text-text-tertiary">
                                                  #{instance?.instance_number || instIndex + 1}
                                                </span>
                                                <span className="text-xs text-text-tertiary">|</span>
                                                <span className="text-xs text-text-secondary truncate max-w-[100px]">
                                                  {instance?.contractor?.full_name || 'Unassigned'}
                                                </span>
                                              </div>
                                            </td>
                                            {/* Status */}
                                            <td className="px-3 py-1.5">
                                              <span className={cn(
                                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                                                getTimesheetStatusColor(status)
                                              )}>
                                                {formatStatusText(status)}
                                              </span>
                                            </td>
                                            {/* Date */}
                                            <td className="px-3 py-1.5 text-xs text-text-secondary">
                                              {formatDate(instance?.scheduled_date, preferences?.date_format)}
                                            </td>
                                            {/* $ Charge */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-primary">
                                              {formatCurrency(instFinancials?.chargeToClient)}
                                            </td>
                                            {/* GST (C) */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-tertiary">
                                              {formatCurrency(instFinancials?.gstOnCharge, false)}
                                            </td>
                                            {/* OT Charge */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-tertiary">
                                              {instFinancials?.hasOvertime
                                                ? formatOvertime(instFinancials.overtimeChargeToClient, instFinancials.clientOvertimeHours, instFinancials.overtimeApproved, instFinancials.overtimePending)
                                                : '-'}
                                            </td>
                                            {/* Commission */}
                                            {hasPartnerAgencyJobs && (
                                              <td className="px-3 py-1.5 text-right text-xs text-text-tertiary">
                                                {instFinancials?.hasCommission ? formatCurrency(instFinancials.commissionMargin, false) : '-'}
                                              </td>
                                            )}
                                            {/* $ Payout */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-primary">
                                              {formatCurrency(instFinancials?.payoutToContractor)}
                                            </td>
                                            {/* GST (P) */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-tertiary">
                                              {formatCurrency(instFinancials?.gstOnPayout, false)}
                                            </td>
                                            {/* OT Payout */}
                                            <td className="px-3 py-1.5 text-right text-xs text-text-tertiary">
                                              {instFinancials?.hasOvertime
                                                ? formatOvertime(instFinancials.overtimePayoutToContractor, instFinancials.contractorOvertimeHours, instFinancials.overtimeApproved, instFinancials.overtimePending)
                                                : '-'}
                                            </td>
                                            {/* Profit */}
                                            {showProfitColumn && (
                                              <td className={`px-3 py-1.5 text-right text-xs ${(instFinancials?.profit || 0) < 0 ? 'text-red-500' : 'text-accent'}`}>
                                                {formatCurrency(instFinancials?.profit)}
                                              </td>
                                            )}
                                            {/* Instance actions */}
                                            <td className="px-3 py-1.5">
                                              <div className="flex items-center justify-end gap-1">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingReport(instance);
                                                  }}
                                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                                  title="View report"
                                                >
                                                  <Eye className="w-4 h-4 text-text-secondary" />
                                                </button>
                                                {status === 'submitted' && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(instance?.id);
                                                      }}
                                                      className="p-1 hover:bg-green-500/20 rounded transition-colors"
                                                      title="Approve"
                                                    >
                                                      <Check className="w-4 h-4 text-green-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRejectingInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                                      title="Reject"
                                                    >
                                                      <XCircle className="w-4 h-4 text-red-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisputingInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-yellow-500/20 rounded transition-colors"
                                                      title="Dispute"
                                                    >
                                                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                                    </button>
                                                  </>
                                                )}
                                                {/* AP Status - View Proof (when paid) or Mark as Paid (when approved) */}
                                                {status === 'paid' && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setViewingPaymentDetails(instance?.id);
                                                    }}
                                                    className="p-1 rounded transition-colors hover:bg-purple-500/20"
                                                    title="View payment proof"
                                                  >
                                                    <Receipt className="w-4 h-4 text-purple-400" />
                                                  </button>
                                                )}
                                                {status === 'approved' && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSinglePaymentInstance(instance);
                                                      setShowRecordPaymentModal(true);
                                                    }}
                                                    className="p-1 rounded transition-colors hover:bg-purple-500/20"
                                                    title="Mark contractor paid"
                                                  >
                                                    <Wallet className="w-4 h-4 text-purple-400" />
                                                  </button>
                                                )}
                                                {/* AR Status Toggle - Client Payment */}
                                                {(status === 'approved' || status === 'paid') && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const isCollected = instance?.client_payment_received;
                                                      if (isCollected) {
                                                        handleMarkAsOutstanding(instance?.id);
                                                      } else {
                                                        handleMarkAsCollected(instance?.id);
                                                      }
                                                    }}
                                                    className={cn(
                                                      'p-1 rounded transition-colors',
                                                      instance?.client_payment_received ? 'hover:bg-gray-500/20' : 'hover:bg-blue-500/20'
                                                    )}
                                                    title={instance?.client_payment_received ? 'Mark client invoice outstanding' : 'Mark client invoice collected'}
                                                  >
                                                    {instance?.client_payment_received ? (
                                                      <ReceiptText className="w-4 h-4 text-gray-400" />
                                                    ) : (
                                                      <Receipt className="w-4 h-4 text-blue-400" />
                                                    )}
                                                  </button>
                                                )}
                                                {/* Charge Adjustment Buttons */}
                                                {(status === 'approved' || status === 'paid') && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAdjustingChargeInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-blue-500/20 rounded transition-colors"
                                                      title="Adjust charge"
                                                    >
                                                      <Settings2 className="w-4 h-4 text-blue-400" />
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAddingChargesInstance(instance);
                                                      }}
                                                      className="p-1 hover:bg-green-500/20 rounded transition-colors"
                                                      title="Add charges"
                                                    >
                                                      <Plus className="w-4 h-4 text-green-400" />
                                                    </button>
                                                  </>
                                                )}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingInstanceInvoice({ job, instance });
                                                  }}
                                                  className="p-1 hover:bg-white/10 rounded transition-colors"
                                                  title="View invoice"
                                                >
                                                  <FileText className="w-4 h-4 text-text-tertiary" />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Report Detail Modal */}
      {viewingReport && (
        <ReportDetailModal
          instance={viewingReport}
          onClose={() => setViewingReport(null)}
          onApprove={() => {
            handleApprove(viewingReport.id);
            setViewingReport(null);
          }}
          onReject={() => {
            setRejectingInstance(viewingReport);
            setViewingReport(null);
          }}
          onDispute={() => {
            setDisputingInstance(viewingReport);
            setViewingReport(null);
          }}
          onMarkPaid={() => {
            setSinglePaymentInstance(viewingReport);
            setShowRecordPaymentModal(true);
            setViewingReport(null);
          }}
        />
      )}

      {/* Reject Modal */}
      {rejectingInstance && (
        <RejectModal
          instance={rejectingInstance}
          onClose={() => setRejectingInstance(null)}
          onReject={(reason) => handleReject(rejectingInstance.id, reason)}
        />
      )}

      {/* Dispute Modal */}
      {disputingInstance && (
        <DisputeModal
          instance={disputingInstance}
          onClose={() => setDisputingInstance(null)}
          onDispute={(reason, category) => handleDispute(disputingInstance.id, reason, category)}
        />
      )}

      {/* Job Invoice Modal (for job-level view) */}
      {viewingJobInvoice && (() => {
        // For one-time jobs (single instance), pass the instance and report data
        const isSingleInstance = viewingJobInvoice.instances?.length === 1;
        const singleInstance = isSingleInstance ? viewingJobInvoice.instances[0] : null;
        const singleTimesheet = singleInstance?.timesheet?.[0] || singleInstance?.timesheet;

        return (
          <Invoice
            job={transformJobForInvoice(viewingJobInvoice.job, viewingJobInvoice.instances)}
            instance={isSingleInstance ? transformInstanceForInvoice(singleInstance) : null}
            company={currentCompany}
            onClose={() => setViewingJobInvoice(null)}
            // Pass report data for single-instance jobs (one-time jobs)
            timesheet={isSingleInstance ? singleTimesheet : null}
            workNotes={isSingleInstance ? singleInstance?.work_notes : null}
            checklistData={isSingleInstance ? singleInstance?.checklist_data : null}
            questionnaireResponses={isSingleInstance ? singleInstance?.questionnaire_responses : null}
            photos={isSingleInstance ? (singleInstance?.photos || []) : []}
            partnerAgency={viewingJobInvoice.job?.partner_agency}
            additionalCharges={isSingleInstance ? (singleInstance?.additional_charges || []) : []}
            companyGstRate={currentCompany?.gst_rate || 10}
          />
        );
      })()}

      {/* Instance Invoice Modal (for instance-level view) */}
      {viewingInstanceInvoice && (
        <Invoice
          job={transformJobForInvoice(viewingInstanceInvoice.job, [viewingInstanceInvoice.instance])}
          instance={transformInstanceForInvoice(viewingInstanceInvoice.instance)}
          company={currentCompany}
          onClose={() => setViewingInstanceInvoice(null)}
          // Report data for completed jobs - prefer passed timesheet, then nested timesheet
          timesheet={viewingInstanceInvoice.timesheet || viewingInstanceInvoice.instance?.timesheet?.[0] || viewingInstanceInvoice.instance?.timesheet}
          workNotes={viewingInstanceInvoice.instance?.work_notes}
          checklistData={viewingInstanceInvoice.instance?.checklist_data}
          questionnaireResponses={viewingInstanceInvoice.instance?.questionnaire_responses}
          photos={viewingInstanceInvoice.instance?.photos || []}
          partnerAgency={viewingInstanceInvoice.job?.partner_agency}
          additionalCharges={viewingInstanceInvoice.instance?.additional_charges || []}
          companyGstRate={currentCompany?.gst_rate || 10}
        />
      )}

      {/* Overtime Approval Modal */}
      {reviewingOvertime && (
        <OvertimeApprovalModal
          request={reviewingOvertime}
          onApprove={handleOvertimeApprove}
          onReject={handleOvertimeReject}
          onClose={() => setReviewingOvertime(null)}
          submitting={overtimeProcessing}
        />
      )}

      {/* Charge Adjustment Modal */}
      {adjustingChargeInstance && (
        <ChargeAdjustmentModal
          instance={adjustingChargeInstance}
          job={adjustingChargeInstance?.job}
          originalCharge={instanceFinancials.get(adjustingChargeInstance?.id)?.chargeToClient || 0}
          companyGstRate={companyGstRate}
          onClose={() => setAdjustingChargeInstance(null)}
          onAdjust={handleChargeAdjustment}
          submitting={chargeSubmitting}
        />
      )}

      {/* Additional Charges Modal */}
      {addingChargesInstance && (
        <AdditionalChargesModal
          instance={addingChargesInstance}
          job={addingChargesInstance?.job}
          existingCharges={addingChargesInstance?.additional_charges || []}
          companyGstRate={companyGstRate}
          onClose={() => setAddingChargesInstance(null)}
          onAddCharge={handleAddCharge}
          onUpdateCharge={handleUpdateCharge}
          onDeleteCharge={handleDeleteCharge}
        />
      )}

      {/* Record Payment Modal (supports both single and bulk) */}
      {showRecordPaymentModal && (() => {
        // Calculate values for single instance if applicable
        const isSinglePayment = !!singlePaymentInstance;
        let instanceIds, totalAmount, jobCount, jobDetails;

        if (isSinglePayment) {
          const financials = calculateInstanceFinancials(singlePaymentInstance, singlePaymentInstance?.job, companyGstRate);
          const basePayout = financials.payoutToContractor || 0;
          const overtimePayout = financials.hasOvertimeApproved ? (financials.overtimePayoutToContractor || 0) : 0;
          const instanceAmount = basePayout + overtimePayout;
          instanceIds = [singlePaymentInstance.id];
          totalAmount = instanceAmount;
          jobCount = 1;
          jobDetails = [{
            id: singlePaymentInstance.id,
            jobName: singlePaymentInstance.job?.job_name || 'Unnamed Job',
            jobDisplayId: singlePaymentInstance.job?.job_display_id,
            instanceNumber: singlePaymentInstance.instance_number,
            contractorName: singlePaymentInstance.contractor?.full_name || 'Unknown',
            scheduledDate: singlePaymentInstance.scheduled_date,
            amount: instanceAmount
          }];
        } else {
          instanceIds = selectedApprovedForPayment.instanceIds;
          totalAmount = selectedApprovedForPayment.total;
          jobCount = selectedApprovedForPayment.count;
          // Build job details from selected instances
          jobDetails = selectedApprovedForPayment.instances.map(inst => {
            const financials = calculateInstanceFinancials(inst, inst?.job, companyGstRate);
            const basePayout = financials.payoutToContractor || 0;
            const overtimePayout = financials.hasOvertimeApproved ? (financials.overtimePayoutToContractor || 0) : 0;
            return {
              id: inst.id,
              jobName: inst.job?.job_name || 'Unnamed Job',
              jobDisplayId: inst.job?.job_display_id,
              instanceNumber: inst.instance_number,
              contractorName: inst.contractor?.full_name || 'Unknown',
              scheduledDate: inst.scheduled_date,
              amount: basePayout + overtimePayout
            };
          });
        }

        return (
          <RecordPaymentModal
            selectedInstances={instanceIds}
            totalAmount={totalAmount}
            jobCount={jobCount}
            jobDetails={jobDetails}
            companyId={currentCompany?.id}
            onClose={() => {
              setShowRecordPaymentModal(false);
              setSinglePaymentInstance(null);
            }}
            onSuccess={handleBulkPaymentSuccess}
          />
        );
      })()}

      {/* Payment Details Modal (View Proof) */}
      {viewingPaymentDetails && (
        <PaymentDetailsModal
          jobInstanceId={viewingPaymentDetails}
          onClose={() => setViewingPaymentDetails(null)}
          onRevert={() => {
            setViewingPaymentDetails(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
